import { useEffect, useRef } from "react";
import { ErrorBoundary, injectStores } from "@intrasync/react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Link,
  Paper,
  Step,
  StepButton,
  Stepper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward, ExpandLess, ExpandMore, Lock } from "@mui/icons-material";
import type DataMapperWizardStore from "./DataMapperWizardStore";
import type { WizardStepView } from "./DataMapperWizardStore";

interface WizardProps {
  dataMapperWizardViewStore?: DataMapperWizardStore;
}

/** The page column the step screens use (DataMapperFrame's width). */
const Column = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 2 }}>
    <Box sx={{ maxWidth: 1400, width: "100%", minWidth: 0 }}>{children}</Box>
  </Box>
);

const stepCaption = (step: WizardStepView): string | undefined => {
  switch (step.state) {
    case "done":
      return "Done";
    case "optional":
      return "Optional";
    case "locked":
      return "Open a profile first";
    default:
      return step.def.optional ? "Optional" : undefined;
  }
};

/** The numbered steps. Each opens its step; a locked one opens Profiles. */
const WizardStepper = ({ store }: { store: DataMapperWizardStore }) => {
  const steps = store.steps;
  const titanSplit = store.isTitan ? steps.findIndex((s) => s.def.key === "schema") : -1;
  const strip = useRef<HTMLDivElement | null>(null);
  const currentIndex = store.currentIndex;
  // On a narrow screen the strip scrolls sideways: keep the open step in view. Layout only, no data.
  useEffect(() => {
    const box = strip.current;
    const current = box?.querySelector<HTMLElement>('[aria-current="step"]');
    if (box && current && box.scrollWidth > box.clientWidth) {
      const left = current.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
      box.scrollLeft = Math.max(0, left - (box.clientWidth - current.offsetWidth) / 2);
    }
  }, [currentIndex]);
  return (
    <Box ref={strip} sx={{ overflowX: "auto", pb: 1 }}>
      <Stepper nonLinear activeStep={store.currentIndex} alternativeLabel sx={{ minWidth: steps.length * 92 }}>
        {steps.map((step) => (
          <Step
            key={step.def.key}
            completed={step.state === "done"}
            disabled={false}
            sx={step.index === titanSplit ? { borderLeft: 1, borderColor: "divider" } : undefined}
          >
            <StepButton
              onClick={() => store.goToStep(step.def.key)}
              aria-label={`Step ${step.index + 1}: ${step.def.label}${stepCaption(step) ? ` (${stepCaption(step)})` : ""}`}
              aria-current={step.state === "current" ? "step" : undefined}
              icon={step.state === "locked" ? <Lock fontSize="small" color="disabled" /> : undefined}
              optional={
                stepCaption(step) ? (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {stepCaption(step)}
                  </Typography>
                ) : undefined
              }
            >
              {step.def.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {titanSplit > 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", textAlign: "right", pr: 1 }}>
          Steps {titanSplit + 1} to {steps.length} map tables by hand. Titan profiles need them only for data Bulk Export does not cover.
        </Typography>
      )}
    </Box>
  );
};

const BackNext = ({ store, compact }: { store: DataMapperWizardStore; compact?: boolean }) => {
  const back = store.previousStep;
  const next = store.nextStep;
  const nextLocked = next?.state === "locked";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      {back && (
        <Button
          variant="outlined"
          size={compact ? "small" : "medium"}
          startIcon={<ArrowBack />}
          onClick={() => store.back()}
          aria-label={`Back to step ${back.index + 1}: ${back.def.label}`}
        >
          Back
        </Button>
      )}
      {next && (
        <Button
          variant="contained"
          size={compact ? "small" : "medium"}
          endIcon={<ArrowForward />}
          disabled={nextLocked}
          onClick={() => store.next()}
          aria-label={`Next: step ${next.index + 1}, ${next.def.label}`}
          title={nextLocked ? "Open a profile first" : undefined}
        >
          Next: {next.def.label}
        </Button>
      )}
    </Box>
  );
};

const DataMapperWizardHeaderInner = ({ dataMapperWizardViewStore }: WizardProps) => {
  const store = dataMapperWizardViewStore;
  if (!store || !store.canUse) return null;
  const step = store.currentStep;
  if (!step) return null;
  const pages = store.currentPages;
  const currentRoute = store.currentRouteName;
  const tabValue = pages.some((p) => p.route === currentRoute) ? currentRoute : false;
  const blockers = store.blockers;
  const notice = step.def.dataNotice;

  return (
    <ErrorBoundary name="DataMapperWizardHeader">
      <Column>
        <Paper component="section" aria-label="Data Mapper steps" sx={{ p: 2 }}>
          <WizardStepper store={store} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mt: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 420px" }}>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Step {step.index + 1} of {store.steps.length}
                {step.def.optional ? " · optional" : ""}
              </Typography>
              <Typography variant="h6" component="h2" sx={{ fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>
                {step.def.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {step.def.summary}
              </Typography>
            </Box>
            <BackNext store={store} compact />
          </Box>

          <Box sx={{ mt: 1 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => store.toggleHelp()}
              aria-expanded={!store.helpCollapsed}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {store.helpCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
              {store.helpCollapsed ? "Show how to do this step" : "Hide how to do this step"}
            </Link>
            <Collapse in={!store.helpCollapsed}>
              <Box component="ol" aria-label="How to do this step" sx={{ m: 0, mt: 1, pl: 3 }}>
                {step.def.instructions.map((line) => (
                  <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>

          {blockers.length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {blockers.map((b) => (
                <Box key={b.text} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span>{b.text}</span>
                  <Link component="button" type="button" variant="body2" onClick={() => store.goToStep(b.step)}>
                    Go to {store.steps.find((s) => s.def.key === b.step)?.def.label ?? "that step"}
                  </Link>
                </Box>
              ))}
            </Alert>
          )}

          {notice && (
            <Alert severity="warning" sx={{ mt: 1.5 }} aria-label={notice.title}>
              <AlertTitle sx={{ mb: 0 }}>{notice.title}</AlertTitle>
              {notice.text}
            </Alert>
          )}

          {pages.length > 1 && (
            <Tabs
              value={tabValue}
              onChange={(_e, route: string) => void store.goToRoute(route)}
              aria-label={`${step.def.label} pages`}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mt: 1.5, borderBottom: 1, borderColor: "divider" }}
            >
              {pages.map((p) => (
                <Tab key={p.route} value={p.route} label={p.label} />
              ))}
            </Tabs>
          )}
        </Paper>
      </Column>
    </ErrorBoundary>
  );
};

/** The stepper, the step's instructions and its pages, above every Data Mapper screen. */
export const DataMapperWizardHeader = injectStores(DataMapperWizardHeaderInner, {
  stores: ["dataMapperWizardViewStore", "routerStore"],
});

const DataMapperWizardFooterInner = ({ dataMapperWizardViewStore }: WizardProps) => {
  const store = dataMapperWizardViewStore;
  if (!store || !store.canUse || !store.currentStep) return null;
  return (
    <ErrorBoundary name="DataMapperWizardFooter">
      <Column>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <BackNext store={store} />
        </Box>
      </Column>
    </ErrorBoundary>
  );
};

/** Back / Next again below the screen, where a long step ends. */
export const DataMapperWizardFooter = injectStores(DataMapperWizardFooterInner, {
  stores: ["dataMapperWizardViewStore", "routerStore"],
});
